import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { searchMovies } from '../lib/tmdb'
import { track, Events } from '../lib/analytics'
import Logo from '../components/Logo'
import Page from '../components/Page'
import MovieWheel from '../components/MovieWheel'

const MIN_PICKS = 2
const MAX_PICKS = 12

export default function SoloSpin() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(new Set())
  const [phase, setPhase] = useState('picking')
  const [showAddModal, setShowAddModal] = useState(false)
  const [toast, setToast] = useState('')

  useEffect(() => {
    loadWatchlist()
  }, [user])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 2400)
    return () => clearTimeout(t)
  }, [toast])

  async function loadWatchlist() {
    if (!user) return
    setLoading(true)
    const { data } = await supabase
      .from('watchlist_items')
      .select('*, movies(*)')
      .eq('user_id', user.id)
      .order('added_at', { ascending: false })
    setItems(data || [])
    setLoading(false)
  }

  function toggleSelect(movieId) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(movieId)) {
        next.delete(movieId)
      } else {
        if (next.size >= MAX_PICKS) {
          setToast(`Max ${MAX_PICKS} picks — deselect one first.`)
          return prev
        }
        next.add(movieId)
      }
      return next
    })
  }

  function startSpin() {
    if (selected.size < MIN_PICKS) return
    if (selected.size > MAX_PICKS) {
      setToast(`Max ${MAX_PICKS} picks — deselect some first.`)
      return
    }
    track(Events.WheelSpun, { mode: 'solo', picks: selected.size })
    setPhase('spinning')
  }

  function handleWinner(movie) {
    setTimeout(() => {
      navigate('/solo/result', {
        state: {
          movie,
          selectedMovies,
        },
      })
    }, 1400)
  }

  const selectedMovies = items
    .filter(i => selected.has(i.movies?.id))
    .map(i => i.movies)
    .filter(Boolean)

  const count = selected.size
  const canSpin = count >= MIN_PICKS && count <= MAX_PICKS
  const hasWatchlist = items.length > 0
  const tooFewMovies = items.length < MIN_PICKS

  if (phase === 'spinning') {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center px-4 relative">
        <button
          onClick={() => setPhase('picking')}
          className="absolute top-5 left-5 text-sm font-semibold text-[var(--fp-text-muted)] bg-transparent border-none cursor-pointer hover:text-white transition-colors"
        >
          ← Back
        </button>
        <MovieWheel
          movies={selectedMovies}
          onWinner={handleWinner}
          subtitle={`${selectedMovies.length} picks`}
        />
      </div>
    )
  }

  return (
    <Page mode="dashboard">
      <div className="flex items-center justify-between py-4 mb-10">
        <Logo size="md" />
        <button
          onClick={() => navigate('/spin-mode')}
          className="text-sm font-semibold text-[var(--fp-red)] bg-transparent border-none cursor-pointer"
        >
          Cancel
        </button>
      </div>

      <h1 className="fp-display text-3xl text-white mb-2">Your Solo Spin</h1>
      <p className="text-sm text-[var(--fp-text-muted)] mb-8">
        Pick {MIN_PICKS}–{MAX_PICKS} movies. Wheel decides.
      </p>

      {loading ? (
        <p className="text-center text-[var(--fp-text-muted)] py-12">Loading…</p>
      ) : tooFewMovies ? (
        <EmptyState
          onAdd={() => setShowAddModal(true)}
          count={items.length}
          min={MIN_PICKS}
        />
      ) : (
        <PickerGrid
          items={items}
          selected={selected}
          onToggle={toggleSelect}
          onAdd={() => setShowAddModal(true)}
        />
      )}

      {/* Sticky footer with count + spin */}
      {hasWatchlist && !tooFewMovies && (
        <div className="fixed bottom-0 left-0 right-0 z-20 px-6 py-5" style={{
          background: 'linear-gradient(180deg, transparent, var(--fp-bg) 40%)',
        }}>
          <div className="max-w-2xl mx-auto flex items-center gap-4">
            <div
              className="flex-1 text-center text-sm font-semibold"
              style={{ color: canSpin ? 'var(--fp-gold)' : 'var(--fp-text-muted)' }}
            >
              {count} of {MAX_PICKS} · min {MIN_PICKS}
            </div>
            <button
              onClick={startSpin}
              disabled={!canSpin}
              className="px-10 py-4 rounded-full border-none text-white text-base font-bold tracking-wide cursor-pointer disabled:opacity-40"
              style={{
                background: 'var(--fp-red)',
                boxShadow: '0 8px 32px var(--fp-red-glow)',
                minHeight: '48px',
              }}
            >
              Spin the wheel
            </button>
          </div>
        </div>
      )}

      {toast && (
        <div
          className="fixed left-1/2 -translate-x-1/2 z-40 px-5 py-3 rounded-full text-sm font-semibold text-white shadow-lg"
          style={{
            background: 'var(--fp-red)',
            boxShadow: '0 8px 24px var(--fp-red-glow)',
            bottom: 'calc(env(safe-area-inset-bottom, 16px) + 110px)',
            maxWidth: '90vw',
          }}
        >
          {toast}
        </div>
      )}

      {showAddModal && (
        <AddMovieModal
          userId={user.id}
          onClose={() => setShowAddModal(false)}
          onAdded={async (movieRecord) => {
            await loadWatchlist()
            setSelected(prev => {
              if (prev.size >= MAX_PICKS) return prev
              const next = new Set(prev)
              next.add(movieRecord.id)
              return next
            })
          }}
        />
      )}
    </Page>
  )
}

function EmptyState({ onAdd, count, min }) {
  return (
    <div className="fp-card-elevated text-center py-16">
      <div className="text-5xl mb-4">🎬</div>
      <h2 className="fp-display text-xl text-white mb-2">
        {count === 0 ? 'Your watchlist is empty' : 'Almost there'}
      </h2>
      <p className="text-sm text-[var(--fp-text-muted)] mb-8 max-w-xs mx-auto">
        Add at least {min} movies to spin.
      </p>
      <button
        onClick={onAdd}
        className="fp-btn fp-btn-primary max-w-xs mx-auto"
      >
        Search for a movie
      </button>
    </div>
  )
}

function PickerGrid({ items, selected, onToggle, onAdd }) {
  return (
    <>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-6" style={{ paddingBottom: '140px' }}>
        {items.map(item => {
          const m = item.movies
          if (!m) return null
          const isSelected = selected.has(m.id)
          return (
            <button
              key={item.id}
              onClick={() => onToggle(m.id)}
              className="relative rounded-xl overflow-hidden bg-[var(--fp-border)] border-none p-0 cursor-pointer transition-all"
              style={{
                aspectRatio: '2/3',
                boxShadow: isSelected
                  ? '0 0 0 3px var(--fp-gold), 0 8px 24px rgba(212, 175, 55, 0.35)'
                  : 'none',
                transform: isSelected ? 'scale(0.97)' : 'scale(1)',
                minHeight: '44px',
              }}
            >
              {m.poster_url ? (
                <img src={m.poster_url} alt={m.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-3xl">🎬</div>
              )}
              {isSelected && (
                <div
                  className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center text-black font-bold text-sm"
                  style={{ background: 'var(--fp-gold)' }}
                >
                  ✓
                </div>
              )}
              <div
                className="absolute inset-x-0 bottom-0 px-2 py-2 text-[10px] font-semibold text-white text-left truncate"
                style={{
                  background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)',
                }}
              >
                {m.title}
              </div>
            </button>
          )
        })}

        <button
          onClick={onAdd}
          className="rounded-xl border-2 border-dashed border-[var(--fp-border-accent)] bg-transparent cursor-pointer flex flex-col items-center justify-center text-[var(--fp-text-muted)] hover:border-[var(--fp-red)] hover:text-white transition-colors"
          style={{ aspectRatio: '2/3', minHeight: '44px' }}
        >
          <span className="text-3xl mb-1">+</span>
          <span className="text-xs font-semibold">Add more</span>
        </button>
      </div>
    </>
  )
}

function AddMovieModal({ userId, onClose, onAdded }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [adding, setAdding] = useState(null)
  const debounceRef = useRef(null)

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (query.length < 2) { setResults([]); return }

    debounceRef.current = setTimeout(async () => {
      setSearching(true)
      const movies = await searchMovies(query)
      setResults(movies)
      setSearching(false)
    }, 350)

    return () => clearTimeout(debounceRef.current)
  }, [query])

  async function handleSelect(movie) {
    setAdding(movie.tmdb_id)
    try {
      const upsertData = {
        tmdb_id: movie.tmdb_id,
        title: movie.title,
        poster_url: movie.poster_url,
        year: movie.year,
        genre: movie.genre,
        runtime_minutes: movie.runtime_minutes,
        tmdb_rating: movie.tmdb_rating,
        overview: movie.overview,
      }
      if (movie.genres?.length) upsertData.genres = movie.genres

      const { data: movieRecord, error: movieErr } = await supabase
        .from('movies')
        .upsert(upsertData, { onConflict: 'tmdb_id' })
        .select()
        .single()
      if (movieErr) throw movieErr

      const { data: existing } = await supabase
        .from('watchlist_items')
        .select('id')
        .eq('user_id', userId)
        .eq('movie_id', movieRecord.id)
        .maybeSingle()

      if (!existing) {
        await supabase.from('watchlist_items').insert({
          user_id: userId,
          movie_id: movieRecord.id,
        })
      }

      await onAdded(movieRecord)
      setQuery('')
      setResults([])
    } catch (err) {
      console.error(err)
      alert('Failed to add. Try again.')
    } finally {
      setAdding(null)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center px-4 py-8"
      style={{ background: 'rgba(0,0,0,0.85)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-[var(--fp-card)] border border-[var(--fp-border)] rounded-2xl p-6 mt-8"
        style={{ maxHeight: '85vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="fp-display text-xl text-white">Add a movie</h2>
          <button
            onClick={onClose}
            className="text-sm font-semibold text-[var(--fp-text-muted)] bg-transparent border-none cursor-pointer hover:text-white transition-colors"
          >
            Done
          </button>
        </div>

        <div className="relative mb-5">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg text-[var(--fp-text-dim)]">🔍</span>
          <input
            type="text"
            placeholder="Search movies…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="fp-input pl-12 text-base"
            style={{ padding: '16px 18px 16px 48px', fontSize: '16px' }}
            autoFocus
          />
        </div>

        {searching && <p className="text-sm text-[var(--fp-text-muted)] text-center py-6">Searching…</p>}

        {results.length > 0 && (
          <div className="fp-card overflow-hidden">
            {results.map(movie => (
              <button
                key={movie.tmdb_id}
                onClick={() => handleSelect(movie)}
                disabled={adding === movie.tmdb_id}
                className="w-full flex gap-4 py-4 px-2 border-b border-[var(--fp-border)] last:border-b-0 first:pt-0 last:pb-0 bg-transparent text-left cursor-pointer hover:bg-[var(--fp-elevated)] transition-colors disabled:opacity-50"
              >
                <div className="w-12 h-18 rounded-lg overflow-hidden flex-shrink-0 bg-[var(--fp-border)]" style={{ aspectRatio: '2/3', width: '48px' }}>
                  {movie.poster_url ? (
                    <img src={movie.poster_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">🎬</div>
                  )}
                </div>
                <div className="flex-1 min-w-0 flex flex-col justify-center">
                  <div className="text-sm font-semibold text-white truncate">{movie.title}</div>
                  <div className="text-xs text-[var(--fp-text-muted)] mt-1">
                    {[movie.year, movie.genre].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <span className="self-center text-sm font-semibold" style={{ color: 'var(--fp-gold)' }}>
                  {adding === movie.tmdb_id ? '…' : '+ Add'}
                </span>
              </button>
            ))}
          </div>
        )}

        {query.length >= 2 && !searching && results.length === 0 && (
          <p className="text-sm text-[var(--fp-text-muted)] text-center py-8">
            Nothing found for "{query}"
          </p>
        )}
      </div>
    </div>
  )
}
